import { useState, useEffect } from 'react';
import { api } from '../lib/api-client';
import { AppType } from '../hooks/useNavigation';
import Breadcrumbs from '../components/navigation/Breadcrumbs';
import BackButton from '../components/navigation/BackButton';
import MarkdownRenderer from '../components/content/MarkdownRenderer';

interface TopicPageProps {
  topicId: string;
  sectionId: string;
  currentApp: AppType;
  goHome: () => void;
  goToSection: (id: string) => void;
  isHI?: boolean;
  goHIHome?: () => void;
}

export default function TopicPage({
  topicId,
  sectionId,
  goHome,
  goToSection,
  isHI,
  goHIHome,
}: TopicPageProps) {
  const [article, setArticle] = useState<any>(null);
  const [section, setSection] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getArticle(topicId).then((data) => {
      if (!cancelled) {
        setArticle(data.article);
        setSection(data.section);
        setLoading(false);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(err.message || 'Article not found');
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [topicId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-content-border border-t-oak-green rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center py-20">
        <div className="text-content-secondary mb-2">Article not found or not yet published.</div>
        <button onClick={goHome} className="text-oak-green hover:underline">
          Go to Home
        </button>
      </div>
    );
  }

  const sectionTitle = section?.title || '';

  // Build breadcrumbs
  const breadcrumbs = [];
  if (isHI) {
    breadcrumbs.push({ label: '🌳 OAK Library', onClick: goHome });
    breadcrumbs.push({ label: '🏥 Health Insurance', onClick: goHIHome || (() => {}) });
    breadcrumbs.push({ label: sectionTitle, onClick: () => goToSection(sectionId) });
    breadcrumbs.push({ label: article.title, onClick: () => {} });
  } else {
    breadcrumbs.push({ label: '🌳 OAK Library', onClick: goHome });
    breadcrumbs.push({ label: sectionTitle, onClick: () => goToSection(sectionId) });
    breadcrumbs.push({ label: article.title, onClick: () => {} });
  }

  const backLabel = `← Back to ${sectionTitle || 'Section'}`;
  const backAction = () => goToSection(sectionId);

  return (
    <div className="max-w-[800px]">
      <Breadcrumbs items={breadcrumbs} />

      <div className="mb-2">
        <span className="bg-oak-green/[0.15] text-oak-green py-1 px-2.5 rounded text-xs font-semibold">
          {isHI ? 'Health Insurance' : 'Core Platform'} › {topicId}
        </span>
      </div>
      <h1 className="text-[28px] font-extrabold text-navy mb-3">{article.title}</h1>
      {article.summary && (
        <p className="text-[15px] text-content-secondary mb-7 leading-relaxed">
          {article.summary}
        </p>
      )}
      {!article.summary && sectionTitle && (
        <p className="text-[15px] text-content-secondary mb-7 leading-relaxed">
          Part of {sectionTitle}.
        </p>
      )}

      <div className="bg-white rounded-xl border border-content-border p-4 sm:p-6">
        {article.body_markdown ? (
          <MarkdownRenderer content={article.body_markdown} />
        ) : (
          <div className="text-content-secondary text-center py-8">
            <p className="mb-4">Content for this topic is being prepared.</p>
            <div className="bg-gradient-to-br from-surface-light to-content-border/50 border-2 border-dashed border-content-border rounded-xl py-12 px-6 text-center">
              <div className="text-[32px] mb-2">📝</div>
              <div className="text-sm text-content-secondary italic">
                Full documentation with step-by-step instructions, screenshots, and examples will be added here.
              </div>
            </div>
          </div>
        )}
      </div>

      <BackButton label={backLabel} onClick={backAction} />
    </div>
  );
}
